import React, { useState, useEffect, useRef } from "react";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiUpload,
  FiUsers,
  FiFileText,
  FiCheck,
  FiPhone,
  FiGlobe,
  FiTag,
  FiUser,
  FiCalendar,
  FiClock,
  FiPlusCircle,
  FiAlertCircle,
  FiSend,
  FiX,
} from "react-icons/fi";

// ── Step Bar ──────────────────────────────────────────────────────────────────
function StepBar({ current }) {
  const steps = [
    { num: 1, label: "CAMPAIGN DETAILS" },
    { num: 2, label: "CHOOSE AUDIENCE" },
    { num: 3, label: "CAMPAIGN TEMPLATE" },
    { num: 4, label: "PREVIEW CAMPAIGN" },
  ];
  return (
    <div className="bg-white border-b border-gray-100 px-6 py-5">
      <div className="max-w-4xl mx-auto flex items-center justify-between relative">
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" />
        <div
          className="absolute top-5 left-0 h-0.5 bg-green-500 z-0 transition-all duration-500"
          style={{ width: `${((current - 1) / 3) * 100}%` }}
        />
        {steps.map((s) => (
          <div key={s.num} className="flex flex-col items-center gap-2 z-10">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300
              ${
                current > s.num
                  ? "bg-green-500 border-green-500 text-white"
                  : current === s.num
                    ? "bg-green-500 border-green-500 text-white shadow-lg shadow-green-200"
                    : "bg-white border-gray-300 text-gray-400"
              }`}
            >
              {current > s.num ? <FiCheck size={16} /> : s.num}
            </div>
            <span
              className={`text-[10px] font-semibold tracking-wider whitespace-nowrap
              ${current >= s.num ? "text-green-600" : "text-gray-400"}`}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Custom Select ─────────────────────────────────────────────────────────────
function CustomSelect({
  label,
  icon: Icon,
  value,
  onChange,
  options,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selected = options.find(
    (o) => (typeof o === "object" ? o.value : o) === value,
  );
  const displayLabel = selected
    ? typeof selected === "object"
      ? String(selected.label ?? selected.value ?? "")
      : String(selected)
    : placeholder;

  return (
    <div>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl bg-white hover:border-green-400 transition-colors text-left"
        >
          {Icon && (
            <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Icon size={13} className="text-gray-500" />
            </div>
          )}
          <span
            className={`flex-1 text-sm ${value ? "text-gray-800 font-medium" : "text-gray-400"}`}
          >
            {displayLabel}
          </span>
          <FiChevronDown
            size={16}
            className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden max-h-48 overflow-y-auto">
            {options.map((opt) => {
              const val = typeof opt === "object" ? opt.value : opt;
              const lbl =
                typeof opt === "object"
                  ? String(opt.label ?? opt.value ?? "")
                  : String(opt);
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => {
                    onChange(val);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 transition-colors ${value === val ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"}`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STEP 1 ────────────────────────────────────────────────────────────────────
function Step1({ data, onChange, brandNumbers, loadingBrands }) {
  const countries = [
    { value: "91", label: "🇮🇳 India (+91)" },
    { value: "1", label: "🇺🇸 USA (+1)" },
    { value: "44", label: "🇬🇧 UK (+44)" },
    { value: "971", label: "🇦🇪 UAE (+971)" },
    { value: "966", label: "🇸🇦 Saudi Arabia (+966)" },
    { value: "65", label: "🇸🇬 Singapore (+65)" },
    { value: "60", label: "🇲🇾 Malaysia (+60)" },
    { value: "61", label: "🇦🇺 Australia (+61)" },
  ];

  const brandOptions = loadingBrands
    ? [{ value: "", label: "Loading numbers..." }]
    : brandNumbers.length === 0
      ? [{ value: "", label: "No numbers found in Meta" }]
      : brandNumbers.map((b) => ({
          value: b.phoneNumberId,
          label: `${b.displayPhoneNumber}${b.verifiedName ? ` (${b.verifiedName})` : ""}`,
        }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Campaign Details</h2>
        <p className="text-sm text-gray-500 mt-1">
          Set up your WhatsApp campaign basics
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomSelect
          label="Campaign Channel"
          icon={FiSend}
          value={data.channel}
          onChange={(v) => onChange("channel", v)}
          options={[{ value: "whatsapp", label: "WhatsApp" }]}
          placeholder="Select Channel"
        />
        <CustomSelect
          label="Select Country"
          icon={FiGlobe}
          value={data.countryCode}
          onChange={(v) => onChange("countryCode", v)}
          options={countries}
          placeholder="Select Country"
        />
        <CustomSelect
          label="Brand Number (From Meta)"
          icon={FiPhone}
          value={data.brandNumber}
          onChange={(v) => onChange("brandNumber", v)}
          options={brandOptions}
          placeholder="Select Brand Number"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CustomSelect
          label="Campaign Type"
          icon={FiTag}
          value={data.campaignType}
          onChange={(v) => onChange("campaignType", v)}
          options={["Normal Campaign", "Scheduled Campaign"]}
          placeholder="Select Type"
        />
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Campaign Name
          </label>
          <div className="relative">
            <FiFileText
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            />
            <input
              type="text"
              value={data.campaignName}
              maxLength={50}
              onChange={(e) => onChange("campaignName", e.target.value)}
              placeholder="e.g. Diwali Offer Campaign"
              className="w-full pl-9 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400 transition-colors"
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-green-600">
              Example: Campaign for remarketing
            </span>
            <span className="text-xs text-gray-400">
              {data.campaignName.length}/50
            </span>
          </div>
        </div>
      </div>
      {/* Assign Agent */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Assign Agent
        </label>
        <div className="flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl bg-white">
          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
            <FiUser size={13} className="text-gray-500" />
          </div>
          <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
            <FiCheck size={11} /> All Agents
            <button className="ml-1 text-green-500 hover:text-green-700">
              <FiX size={11} />
            </button>
          </div>
          <FiChevronDown size={16} className="ml-auto text-gray-400" />
        </div>
      </div>
    </div>
  );
}

// ── STEP 2 ────────────────────────────────────────────────────────────────────
function Step2({ data, onChange }) {
  const [tab, setTab] = useState("paste");
  const [csvFile, setCsvFile] = useState(null);
  const fileRef = useRef(null);

  const parseNumbers = (text) =>
    text
      .split(/[\n,;]+/)
      .map((n) => n.trim())
      .filter((n) => n.length >= 7);

  const handlePasteChange = (val) => {
    onChange("numbersText", val);
    onChange("numbers", parseNumbers(val));
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const nums = ev.target.result.split("\n").flatMap((l) =>
        l
          .split(",")
          .map((n) => n.replace(/\D/g, ""))
          .filter((n) => n.length >= 7),
      );
      onChange("numbers", nums);
      onChange("numbersText", nums.join("\n"));
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Choose Audience</h2>
        <p className="text-sm text-gray-500 mt-1">
          Add contacts to send the campaign on{" "}
          <span className="text-green-600 font-semibold">WhatsApp</span>
        </p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {[
          { id: "paste", label: "Copy Paste" },
          { id: "contacts", label: "Contact List" },
          { id: "csv", label: "Upload CSV" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${tab === t.id ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "paste" && (
        <div>
          <div className="flex items-center gap-2 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <FiAlertCircle size={14} className="text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-700">
              Enter mobile numbers without country code — one per line
            </span>
          </div>
          <textarea
            value={data.numbersText || ""}
            onChange={(e) => handlePasteChange(e.target.value)}
            placeholder={"9899046169\n9876543210\n9812345678"}
            rows={10}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400 resize-none font-mono"
          />
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-400">One number per line</span>
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              Count: {data.numbers?.length || 0}
            </span>
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <FiUsers size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-400">
            Contact list feature coming soon
          </p>
        </div>
      )}

      {tab === "csv" && (
        <div>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all"
          >
            {csvFile ? (
              <div className="flex flex-col items-center text-green-600">
                <FiCheck size={32} className="mb-2" />
                <p className="text-sm font-semibold">{csvFile}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {data.numbers?.length || 0} numbers loaded
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <FiUpload size={32} className="mb-3" />
                <p className="text-sm font-semibold text-gray-600">
                  Click to upload CSV
                </p>
                <p className="text-xs mt-1">Mobile numbers in first column</p>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleCsvUpload}
          />
        </div>
      )}
    </div>
  );
}

// ── STEP 3 ────────────────────────────────────────────────────────────────────
function Step3({ data, onChange, templates }) {
  const selected = templates.find((t) => t._id === data.templateId);

  const getVars = (msg) => {
    if (!msg) return [];
    const regex = /\{\{(\d+)\}\}/g;
    const nums = new Set();
    let match;
    while ((match = regex.exec(msg)) !== null) nums.add(parseInt(match[1]));
    return Array.from(nums).sort((a, b) => a - b);
  };

  const vars = selected ? getVars(selected.message) : [];

  const renderPreview = (msg, varValues) => {
    if (!msg) return "";
    let rendered = msg;
    Object.entries(varValues || {}).forEach(([num, val]) => {
      rendered = rendered.split(`{{${num}}}`).join(val || `{{${num}}}`);
    });
    return rendered;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Campaign Template</h2>
        <p className="text-sm text-gray-500 mt-1">
          Select <span className="text-green-600 font-semibold">WhatsApp</span>{" "}
          template
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <CustomSelect
            label="Select Template"
            icon={FiFileText}
            value={data.templateId}
            onChange={(v) => {
              onChange("templateId", v);
              onChange("variableValues", {});
            }}
            options={templates.map((t) => ({
              value: t._id,
              label: t.templateName,
            }))}
            placeholder="Select Approved Template"
          />

          {selected && vars.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
                ⚡ Same value will be sent to ALL contacts
              </p>
              {vars.map((num) => (
                <div
                  key={num}
                  className="flex items-center gap-3 border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-green-400 transition-colors"
                >
                  <div className="bg-gray-100 px-4 py-3 text-sm font-bold text-gray-600 border-r border-gray-200 flex-shrink-0">
                    {`{{${num}}}`}
                  </div>
                  <input
                    type="text"
                    value={data.variableValues?.[num] || ""}
                    onChange={(e) =>
                      onChange("variableValues", {
                        ...data.variableValues,
                        [num]: e.target.value,
                      })
                    }
                    placeholder={`Value for {{${num}}}`}
                    className="flex-1 px-3 py-3 text-sm outline-none bg-white"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Phone Preview */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">
            Template Preview
          </p>
          <div className="relative mx-auto" style={{ width: 240 }}>
            <div className="bg-gray-900 rounded-[32px] p-1.5 shadow-2xl">
              <div
                className="bg-white rounded-[26px] overflow-hidden"
                style={{ minHeight: 380 }}
              >
                <div className="bg-green-700 px-3 py-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-bold text-green-800">
                    J
                  </div>
                  <p className="text-white text-xs font-semibold">
                    jaiswal softwar...
                  </p>
                </div>
                <div
                  className="p-3 min-h-[260px]"
                  style={{ backgroundColor: "#e5ddd5" }}
                >
                  {selected ? (
                    <div className="bg-white rounded-lg shadow-sm max-w-[90%] p-2.5 text-xs text-gray-700 leading-relaxed">
                      {selected.mediaUrl && selected.mediaType === "image" && (
                        <img
                          src={selected.mediaUrl}
                          alt=""
                          className="w-full rounded mb-2 max-h-20 object-cover"
                        />
                      )}
                      {selected.header && (
                        <p className="font-bold mb-1">{selected.header}</p>
                      )}
                      <p className="whitespace-pre-wrap">
                        {renderPreview(selected.message, data.variableValues)}
                      </p>
                      {selected.footer && (
                        <p className="text-[10px] text-gray-400 mt-1">
                          {selected.footer}
                        </p>
                      )}
                      {selected.buttons?.filter((b) => b.text).length > 0 && (
                        <div className="border-t border-gray-100 mt-2">
                          {selected.buttons
                            .filter((b) => b.text)
                            .map((btn, i) => (
                              <div
                                key={i}
                                className="text-center py-1 text-[10px] font-medium text-blue-500 border-b border-gray-100 last:border-0"
                              >
                                {btn.type === "url"
                                  ? "🔗 "
                                  : btn.type === "phone"
                                    ? "📞 "
                                    : ""}
                                {btn.text}
                              </div>
                            ))}
                        </div>
                      )}
                      <div className="text-right mt-1">
                        <span className="text-[9px] text-gray-300">
                          10:30 ✓✓
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-48 items-center justify-center">
                      <p className="text-[11px] text-gray-400 text-center px-4">
                        Select a template to preview
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STEP 4 ────────────────────────────────────────────────────────────────────
function Step4({ data, onChange, templates, brandNumbers }) {
  const template = templates.find((t) => t._id === data.templateId);
  const brand = brandNumbers.find((b) => b.phoneNumberId === data.brandNumber);

  const infoRows = [
    { label: "Campaign Name", value: data.campaignName || "—", icon: FiTag },
    { label: "Channel", value: data.channel || "—", icon: FiSend },
    {
      label: "Brand Number",
      value: brand?.displayPhoneNumber || data.brandNumber || "—",
      icon: FiPhone,
    },
    {
      label: "Campaign Type",
      value: data.campaignType || "—",
      icon: FiFileText,
    },
    {
      label: "Total Audience",
      value: `${data.numbers?.length || 0} contacts`,
      icon: FiUsers,
    },
    {
      label: "Template",
      value: template?.templateName || "—",
      icon: FiFileText,
    },
    {
      label: "Campaign Date",
      value: new Date().toLocaleDateString("en-IN"),
      icon: FiCalendar,
    },
    {
      label: "Country Code",
      value: `+${data.countryCode || "91"}`,
      icon: FiGlobe,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Preview Campaign</h2>
        <p className="text-sm text-gray-500 mt-1">Review before launching</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {infoRows.map((row) => (
          <div
            key={row.label}
            className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3"
          >
            <row.icon size={16} className="text-green-500 flex-shrink-0" />
            <div>
              <span className="text-xs text-gray-500">{row.label}</span>
              <p className="text-sm font-bold text-gray-800">{row.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Schedule */}
      <div className="border-2 border-gray-200 rounded-xl p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={() => onChange("scheduled", !data.scheduled)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${data.scheduled ? "bg-green-500 border-green-500" : "border-gray-300"}`}
          >
            {data.scheduled && <FiCheck size={12} className="text-white" />}
          </div>
          <div>
            <p className="text-sm font-semibold text-green-600">
              Schedule Campaign
            </p>
            <p className="text-xs text-gray-500">
              Set start and end time for this campaign
            </p>
          </div>
        </label>
        {data.scheduled && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={data.scheduleStart || ""}
                onChange={(e) => onChange("scheduleStart", e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                End Date & Time
              </label>
              <input
                type="datetime-local"
                value={data.scheduleEnd || ""}
                onChange={(e) => onChange("scheduleEnd", e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400"
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
        <FiAlertCircle
          size={16}
          className="text-green-500 flex-shrink-0 mt-0.5"
        />
        <div>
          <p className="text-sm font-semibold text-green-700">
            Ready to Launch 🚀
          </p>
          <p className="text-xs text-green-600 mt-0.5">
            Campaign will be sent to{" "}
            <strong>{data.numbers?.length || 0}</strong> contacts using{" "}
            <strong>"{template?.templateName || "—"}"</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CreateCampaign() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [brandNumbers, setBrandNumbers] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  const [campaign, setCampaign] = useState({
    channel: "whatsapp",
    countryCode: "91",
    brandNumber: "",
    campaignType: "Normal Campaign",
    campaignName: "",
    numbers: [],
    numbersText: "",
    templateId: "",
    variableValues: {},
    scheduled: false,
    scheduleStart: "",
    scheduleEnd: "",
  });

  const onChange = (key, val) =>
    setCampaign((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    // Load approved templates
    axios
      .get("/api/templates?status=Approved&limit=100")
      .then((res) => {
        if (res.data.success) setTemplates(res.data.data);
      })
      .catch(console.error);

    // Load brand numbers from Meta API ← NO MORE HARDCODED
    setLoadingBrands(true);
    axios
      .get("/api/campaigns/brand-numbers")
      .then((res) => {
        if (res.data.success) setBrandNumbers(res.data.data);
      })
      .catch(console.error)
      .finally(() => setLoadingBrands(false));
  }, []);

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!campaign.brandNumber) e.brandNumber = "Please select a brand number";
      if (!campaign.campaignName.trim())
        e.campaignName = "Please enter a campaign name";
    }
    if (step === 2 && !campaign.numbers?.length)
      e.numbers = "Please add at least one number";
    if (step === 3 && !campaign.templateId)
      e.templateId = "Please select a template";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) setStep((s) => Math.min(s + 1, 4));
  };
  const handlePrev = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post("/api/campaigns", {
        campaignName: campaign.campaignName,
        channel: campaign.channel,
        countryCode: campaign.countryCode,
        brandNumber: campaign.brandNumber,
        campaignType: campaign.campaignType,
        numbers: campaign.numbers,
        templateId: campaign.templateId,
        variableValues: campaign.variableValues,
        scheduled: campaign.scheduled,
        scheduleStart: campaign.scheduleStart,
        scheduleEnd: campaign.scheduleEnd,
      });
      if (res.data.success) {
        setSuccessMsg("Campaign launched successfully! 🎉");
        setTimeout(() => navigate("/campaign/report"), 2000);
      }
    } catch (err) {
      setErrors({
        submit: err.response?.data?.error || "Failed to launch campaign",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (successMsg) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheck size={36} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Campaign Launched!
          </h2>
          <p className="text-gray-500">{successMsg}</p>
          <p className="text-sm text-gray-400 mt-2">
            Redirecting to campaign report...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/campaign/report")}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm"
          >
            <FiChevronLeft size={16} />
          </button>
          <h1 className="text-xl font-bold text-gray-800">WhatsApp Campaign</h1>
        </div>
      </div>

      <StepBar current={step} />

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
          {step === 1 && (
            <Step1
              data={campaign}
              onChange={onChange}
              brandNumbers={brandNumbers}
              loadingBrands={loadingBrands}
            />
          )}
          {step === 2 && <Step2 data={campaign} onChange={onChange} />}
          {step === 3 && (
            <Step3 data={campaign} onChange={onChange} templates={templates} />
          )}
          {step === 4 && (
            <Step4
              data={campaign}
              onChange={onChange}
              templates={templates}
              brandNumbers={brandNumbers}
            />
          )}

          {Object.keys(errors).length > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3">
              {Object.values(errors).map((err, i) => (
                <p
                  key={i}
                  className="text-sm text-red-600 flex items-center gap-2"
                >
                  <FiAlertCircle size={13} /> {err}
                </p>
              ))}
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handlePrev}
              disabled={step === 1}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft size={16} /> Previous
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold shadow-sm transition-colors"
              >
                Next <FiChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold shadow-sm disabled:opacity-50 transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{" "}
                    Launching...
                  </>
                ) : (
                  <>
                    <FiSend size={16} /> Launch Campaign
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
